import * as api from '../../api';
import * as logger from '../../logger';

import { useEffect, useState } from 'react';
import {
  sortByDistance,
  sortByName,
} from '../../scenes/Directory/Provider/Common/utils';
import { useSelector } from 'react-redux';
import * as store from '../../reducers/store';
import { findAllProviders } from '../../scenes/Directory/Provider/Common/cache';

export enum SortKinds {
  Distance = 'Distance',
  Alphabetical = 'Alphabetical',
}

export const SORT_KINDS = [SortKinds.Distance, SortKinds.Alphabetical];

export interface UseProvidersReturnValues {
  providers: api.PrevisitProvider.SafeProviderWithPractices[] | undefined;
  supportedLanguages?: api.PrevisitProvider.SupportedLanguageMap;
  supportedLocations?: api.PrevisitProvider.SupportedLocationMap;
  supportedPractices?: api.PrevisitProvider.SupportedPractice[];
  supportedProviderTypes?: api.PrevisitProvider.SupportedProviderTypeMap;
  supportedSexes?: api.PrevisitProvider.SupportedSexMap;
  totalPages?: number;
  pages?: number[];
  hasLocationSearch: boolean;
  isLoading: boolean;
  acceptedPayorWithPlanTypes?: string[];
}

const filtersHaveAnyValues = (
  filterOptions: api.PrevisitProvider.FilterOptions,
) => {
  return Object.keys(filterOptions).some((key) => {
    const value =
      filterOptions[key as keyof api.PrevisitProvider.FilterOptions];
    return Array.isArray(value) ? value.length > 0 : !!value;
  });
};

export const useProviders = ({
  filterOptions,
  pageLimit,
  setCurrentSort,
}: {
  filterOptions: api.PrevisitProvider.FilterOptions;
  pageLimit: number;
  setCurrentSort: (value: SortKinds) => void;
}): UseProvidersReturnValues => {
  const organizationId = useSelector(
    (store: store.All) => store.inbound.organizationId,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLocationSearch, setHasLocationSearch] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>();
  const [providers, setProviders] = useState<
    api.PrevisitProvider.SafeProviderWithPractices[] | undefined
  >(undefined);
  const [supportedLanguages, setSupportedLanguages] =
    useState<api.PrevisitProvider.SupportedLanguageMap>();
  const [supportedLocations, setSupportedLocations] =
    useState<api.PrevisitProvider.SupportedLocationMap>();
  const [supportedPractices, setSupportedPractices] =
    useState<api.PrevisitProvider.SupportedPractice[]>();
  const [supportedProviderTypes, setSupportedProviderTypes] =
    useState<api.PrevisitProvider.SupportedProviderTypeMap>();
  const [supportedSexes, setSupportedSexes] =
    useState<api.PrevisitProvider.SupportedSexMap>();
  const [acceptedPayorWithPlanTypes, setAcceptedPayorWithPlanTypes] =
    useState<string[]>();

  useEffect(() => {
    async function retrieveProviders() {
      setIsLoading(true);
      try {
        const {
          providers,
          supportedLanguages,
          supportedLocations,
          supportedPractices,
          supportedProviderTypes,
          supportedSexes,
          acceptedPayorWithPlanTypes,
        } = !filtersHaveAnyValues(filterOptions)
          ? await findAllProviders(organizationId)
          : await api.client.PrevisitProvider.findAllByOrganizationId(
              organizationId,
              filterOptions,
            );
        setProviders(providers);
        setSupportedLanguages(supportedLanguages);
        setSupportedLocations(supportedLocations);
        setSupportedPractices(supportedPractices);
        setSupportedProviderTypes(supportedProviderTypes);
        setSupportedSexes(supportedSexes);
        setAcceptedPayorWithPlanTypes(acceptedPayorWithPlanTypes);
        const totalPages =
          providers.length > 0 ? Math.ceil(providers.length / pageLimit) : 1;
        setTotalPages(totalPages);
        const hasLocationSearch = providers.some((provider) =>
          provider.practices.some((practice) => !!practice.distance),
        );
        setCurrentSort(
          hasLocationSearch ? SortKinds.Distance : SortKinds.Alphabetical,
        );
        providers.sort(hasLocationSearch ? sortByDistance : sortByName);
        setHasLocationSearch(hasLocationSearch);
      } catch (err) {
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    retrieveProviders();
  }, [
    organizationId,
    filterOptions,
    pageLimit,
    setProviders,
    setCurrentSort,
    setSupportedLanguages,
  ]);

  return {
    providers,
    supportedLanguages,
    supportedLocations,
    supportedPractices,
    supportedProviderTypes,
    supportedSexes,
    totalPages,
    hasLocationSearch,
    isLoading,
    acceptedPayorWithPlanTypes,
  };
};
